import { CircularProgress, TextField } from "@material-ui/core";
import { Button } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Profile } from "../../models/Profile";
import { upsertProfile } from "../../repositories/ProfilesRepository";

const useStyles = makeStyles((theme: Theme) => ({
	title: {
		fontSize: 15,
	},
	modalTitle: {
		fontSize: 15,
		marginTop: theme.spacing(1),
	},
	chip: {
		margin: 2,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const CreateProfile = ({ show, onHide }: Props) => {
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [message, setMessage] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const classes = useStyles();

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<Profile>({ defaultValues: INITIAL_VALUES });

	const onSubmit: SubmitHandler<Profile> = async (data) => {
		setIsSubmitLoading(true);

		const res = await upsertProfile(data.ProfileId as number, data.ProfileName);
		if (!res.ok) setError(res.message);
		else {
			setMessage("The profile was created successfully");
			setOpenSnackbar(true);
			onHide(true);
		}
		setIsSubmitLoading(false);
	};

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);

	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						title="NEW PROFILE"
						open={show}
						handleClose={() => onHide(false)}
						id="create-profile-modal"
						maxWidth="md"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content style={{ padding: "15px 30px" }}>
								<Grid container spacing={1}>
									<Grid item md={12} xs={12}>
										<TextField
											{...register("ProfileName", { required: true })}
											label="Profile name"
											variant="outlined"
											error={!!errors.ProfileName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>{" "}
										{errors.ProfileName?.type === "required" && (
											<span className={classes.errorLabel}>
												Profile name is required
											</span>
										)}
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid>
								</Grid>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
			<Snackbar
				open={openSnackbar}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="success" onClose={handleClose}>
					{message}
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Profile"
			/>
		</>
	);
};
